import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Building2 } from "lucide-react";
import Timesheets from "@/components/reports/Timesheets";

const QUARTERS = [
  { id: 1, label: "Q1 · Jul – Sep", start: [0, 7, 1], end: [0, 9, 30] },
  { id: 2, label: "Q2 · Oct – Dec", start: [0, 10, 1], end: [0, 12, 31] },
  { id: 3, label: "Q3 · Jan – Mar", start: [1, 1, 1], end: [1, 3, 31] },
  { id: 4, label: "Q4 · Apr – Jun", start: [1, 4, 1], end: [1, 6, 30] },
];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (fy, [off, m, d]) => `${fy + off}-${pad(m)}-${pad(d)}`;
const now = new Date();
const CURRENT_FY = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
const FYS = [0, 1, 2, 3, 4].map((i) => CURRENT_FY - i);
const SELECT = "w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand";

function periodRange(type, fy, q, custom) {
  if (type === "custom") return custom;
  if (type === "fy") return { start: `${fy}-07-01`, end: `${fy + 1}-06-30` };
  const quarter = QUARTERS.find((x) => x.id === q);
  return { start: ymd(fy, quarter.start), end: ymd(fy, quarter.end) };
}

export default function Reports() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "timesheets" ? "timesheets" : "tax");
  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Reports" title={tab === "tax" ? "Client Tax Statements" : "Timesheets"}
        description={tab === "tax"
          ? "Statements of completed cleans with GST, so business clients can claim GST credits on their BAS and the expense on their tax return."
          : "Planned vs actual hours from Start job and Mark complete, with on-site GPS checks."} />
      <div className="inline-flex border border-border/60 rounded-sm p-1 mb-8" data-testid="reports-tabs">
        {[["tax", "Tax statements"], ["timesheets", "Timesheets"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`reports-tab-${id}`}
            className={`px-4 py-2 text-sm rounded-sm transition-colors ${tab === id ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
            <span className="gi-text">{label}</span>
          </button>
        ))}
      </div>
      {tab === "tax" ? <TaxStatements params={params} /> : <Timesheets />}
    </div>
  );
}

function TaxStatements({ params }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [type, setType] = useState("quarter");
  const [fy, setFy] = useState(CURRENT_FY);
  const [q, setQ] = useState(1);
  const [custom, setCustom] = useState({ start: `${CURRENT_FY}-07-01`, end: now.toISOString().slice(0, 10) });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/customers").then((r) => setClients(r.data)).catch(() => {}); }, []);
  const range = useMemo(() => periodRange(type, fy, q, custom), [type, fy, q, custom]);
  const business = clients.filter((c) => c.business_name || c.abn);
  const others = clients.filter((c) => !(c.business_name || c.abn));

  useEffect(() => {
    if (!clientId || !range.start || !range.end) { setReport(null); return; }
    api.get("/reports/tax", { params: { customer_id: clientId, ...range } })
      .then((r) => setReport(r.data))
      .catch((e) => { setReport(null); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [clientId, range]);

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get("/reports/tax", { params: { customer_id: clientId, ...range, format }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `tax-statement.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the report");
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="report-filters">
        <div className="space-y-2 md:col-span-2">
          <Label className="label-caps text-muted-foreground">Client</Label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={SELECT} data-testid="report-client-select">
            <option value="">Select a client…</option>
            {business.length > 0 && (
              <optgroup label="Business clients">
                {business.map((c) => <option key={c.id} value={c.id}>{c.business_name || c.name}{c.business_name ? ` (${c.name})` : ""}</option>)}
              </optgroup>
            )}
            <optgroup label="Other clients">
              {others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="space-y-2">
          <Label className="label-caps text-muted-foreground">Period</Label>
          <select value={type} onChange={(e) => setType(e.target.value)} className={SELECT} data-testid="report-period-type">
            <option value="quarter">BAS quarter</option>
            <option value="fy">Financial year</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {type !== "custom" ? (
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Financial year</Label>
            <select value={fy} onChange={(e) => setFy(Number(e.target.value))} className={SELECT} data-testid="report-fy-select">
              {FYS.map((y) => <option key={y} value={y}>FY {y}–{String(y + 1).slice(2)}</option>)}
            </select>
          </div>
        ) : <div />}
        {type === "quarter" && (
          <div className="space-y-2 md:col-span-2">
            <Label className="label-caps text-muted-foreground">Quarter</Label>
            <div className="flex flex-wrap gap-2" data-testid="report-quarters">
              {QUARTERS.map((x) => (
                <button key={x.id} type="button" onClick={() => setQ(x.id)} data-testid={`report-quarter-${x.id}`}
                  className={`px-3 py-2 rounded-sm text-sm border transition-colors ${q === x.id ? "bg-[hsl(330_85%_93%)] border-[hsl(330_60%_86%)] grad-ink" : "border-border/70 text-muted-foreground hover:bg-accent/60"}`}>
                  <span className="gi-text">{x.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {type === "custom" && (
          <>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">From</Label>
              <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid="report-start" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">To</Label>
              <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid="report-end" />
            </div>
          </>
        )}
      </section>

      {!clientId ? (
        <p className="text-sm text-muted-foreground mt-10 text-center" data-testid="report-empty">Choose a client to see their statement.</p>
      ) : report && (
        <ReportPreview report={report} busy={busy} onDownload={download} />
      )}
    </div>
  );
}

function ReportPreview({ report, busy, onDownload }) {
  const { client, totals, rows, period } = report;
  return (
    <section className="mt-8 border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="report-preview">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 font-serif text-2xl"><Building2 className="w-5 h-5 text-brand" />{client.business_name || client.name}</div>
          <div className="text-sm text-muted-foreground mt-1">{client.abn ? `ABN ${client.abn} · ` : "No ABN on file · "}{period.label}</div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onDownload("csv")} disabled={!!busy} data-testid="report-download-csv" className="rounded-sm gap-2">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "csv" ? "Preparing…" : "CSV"}
          </Button>
          <Button onClick={() => onDownload("pdf")} disabled={!!busy} data-testid="report-download-pdf" className="rounded-sm gap-2">
            <FileDown className="w-4 h-4" /> {busy === "pdf" ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="report-totals">
        {[["Services", totals.count], ["Ex GST", money(totals.ex_gst)], ["GST (10%)", money(totals.gst)], ["Total incl. GST", money(totals.total)]].map(([k, v]) => (
          <div key={k} className="rounded-sm bg-[hsl(330_60%_97%)] border border-[hsl(330_40%_92%)] p-4">
            <div className="label-caps text-muted-foreground">{k}</div>
            <div className="font-serif text-2xl mt-1" data-testid={`report-total-${k.split(" ")[0].toLowerCase()}`}>{v}</div>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-6" data-testid="report-no-rows">No completed cleans in this period.</p>
      ) : (
        <div className="overflow-x-auto mt-6">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Service</TableHead><TableHead>Address</TableHead>
              <TableHead className="text-right">Ex GST</TableHead><TableHead className="text-right">GST</TableHead><TableHead className="text-right">Total</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`report-row-${i}`}>
                  <TableCell>{r.date}</TableCell><TableCell>{r.service}</TableCell>
                  <TableCell className="text-muted-foreground">{r.address}</TableCell>
                  <TableCell className="text-right">{money(r.ex_gst)}</TableCell><TableCell className="text-right">{money(r.gst)}</TableCell>
                  <TableCell className="text-right font-medium">{money(r.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-6">Only bookings marked <strong>Completed</strong> are included. Booking prices are treated as ex-GST, with 10% GST added.</p>
    </section>
  );
}
